import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  poLineDashboardAPI,
  type ForecastStabilityData,
  type ForecastStabilityGrain,
} from '../../../lib/api/po-line-dashboard';
import { ForecastMovementChart } from './ForecastMovementChart';
import type { POLineDrilldownFilters } from './poLineDrilldownModes';

const GRAINS: Array<{ id: ForecastStabilityGrain; label: string; sub: string }> = [
  { id: 'month', label: 'Monthly', sub: 'whole horizon' },
  { id: 'week', label: 'Weekly', sub: 'next 26 weeks' },
];

interface ForecastStabilityProps {
  filters?: POLineDrilldownFilters;
}

function fmtDate(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/**
 * Forecast Stability sub-tab (PO-line-grain installs only) — the forecast
 * twin of the Scheduled Dashboard's Demand Stability tab.
 *
 * One forward-looking visualisation: the forecast book per delivery bucket
 * from today onwards, with the qty added/removed per bucket since the copy
 * taken before the current forecast import (per site in a group view). Monthly
 * across the whole horizon by default
 * (most of a forecast book sits beyond the next quarter); weekly for the
 * near term. Honours the dashboard's supplier and part filters.
 */
export default function ForecastStability({ filters }: ForecastStabilityProps) {
  const [grain, setGrain] = useState<ForecastStabilityGrain>('month');
  const [data, setData] = useState<ForecastStabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const customerId = filters?.customer_id;
  const supplierId = filters?.supplier_id;
  const partNumber = filters?.part_number;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    poLineDashboardAPI
      .getForecastStability({
        customer_id: customerId,
        supplier_id: supplierId,
        part_number: partNumber,
        grain,
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Forecast stability fetch failed:', err);
        setError('Could not load forecast stability data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, supplierId, partNumber, grain]);

  if (loading && !data) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  const siteCount = data ? Object.keys(data.forward.baselines ?? {}).length : 0;
  const sitesWithoutCopy = data
    ? Object.values(data.forward.baselines ?? {}).filter((d) => d === null).length
    : 0;

  if (error) {
    return (
      <div className="h-64 flex flex-col items-center justify-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-5 w-5" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Your forecast book, today onwards — and how it moved since the copy
            taken before the current import. Group views add every site up, judged
            site by site. Re-imports of an unchanged forecast net out; only real
            movement counts.
          </p>
          <div className="inline-flex rounded-md border border-slate-200 p-0.5" role="tablist" aria-label="Bucket size">
            {GRAINS.map((g) => {
              const active = g.id === grain;
              return (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setGrain(g.id)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    active ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {g.label}
                  <span className={`ml-1.5 text-[10px] font-normal ${active ? 'text-slate-300' : 'text-slate-400'}`}>{g.sub}</span>
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-xs text-muted-foreground text-right max-w-sm">
          Copies run weekly on the site&apos;s configured day.
          {data?.last_captured
            ? <> Last copy: <span className="font-medium text-foreground">{fmtDate(data.last_captured)}</span>.</>
            : <> <span className="font-medium text-amber-700">No forecast copy yet</span> — movement shows once a copy taken before the current forecast import exists.</>}
          {data?.source === 'none' && data?.last_captured && (
            <> <span className="font-medium text-amber-700">No copy predates the current forecast import yet</span> — movement shows after the next import.</>
          )}
          {data?.source === 'snapshot' && sitesWithoutCopy > 0 && (
            <> <span className="font-medium text-amber-700">{sitesWithoutCopy} of {siteCount} sites have no copy yet</span> — their forecast is counted, their movement is not shown.</>
          )}
        </p>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin" /> Updating…
        </div>
      )}
      <ForecastMovementChart data={data} grain={grain} />
    </div>
  );
}
